import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { RealtimeChannel } from '@supabase/supabase-js';
import useSessionStore from '../state/session';
import {
  fetchConversations,
  invalidateUnreadReads,
  type ConversationRow,
} from '../data/chat';
import { getSupabaseClient } from '../data/supabase';
import { acknowledgeChatRead } from '../api/dashboard';
import { dashboardQueryKey } from '../domain/home/useHomeDashboard';
import { recordRealtimeEvent } from '../lib/realtimeTelemetry';
import { runSupabaseQuery } from '../utils/appErrors';

export const UNREAD_CHAT_QUERY_KEY = ['chat', 'unread-counts'] as const;

type UnreadChatData = {
  conversations: ConversationRow[];
  counts: Record<string, number>;
};

// ---------------------------------------------------------------------------
// Dismissed-conversations sessionStorage helpers
// ---------------------------------------------------------------------------
const DISMISSED_KEY = 'chat:dismissed-convs';

type DismissedMap = Record<string, number>;

function readDismissedMap(): DismissedMap {
  try {
    const raw = sessionStorage.getItem(DISMISSED_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as DismissedMap;
      console.log('[chat-dismiss] sessionStorage read', parsed);
      return parsed;
    }
  } catch {
    /* noop */
  }
  console.log('[chat-dismiss] sessionStorage read (empty)');
  return {};
}

function writeDismissedMap(map: DismissedMap): void {
  try {
    sessionStorage.setItem(DISMISSED_KEY, JSON.stringify(map));
    console.log('[chat-dismiss] sessionStorage write', map);
  } catch {
    /* noop */
  }
}

/**
 * Shared hook that tracks unread direct/group chat message counts.
 *
 * Backed by React Query so every component that calls this hook shares a
 * single cached result. A Supabase realtime subscription invalidates the
 * cache whenever a new message arrives from someone else.
 *
 * Provides `dismissConversation` / `dismissAllConversations` to hide
 * unread indicators WITHOUT marking messages as read. Dismissed state is
 * stored in sessionStorage (resets on new browser session). Dismissals are
 * automatically cleared when a new message arrives from another user or
 * when the conversation is explicitly marked as read.
 */
export function useUnreadChatCounts() {
  const profileId = useSessionStore((s) => s.profileId);
  const sessionReady = useSessionStore((s) => s.sessionReady);
  const queryClient = useQueryClient();

  // ---- Dismissed-conversations state (sessionStorage-backed) ----
  const [dismissed, setDismissed] = React.useState<DismissedMap>(readDismissedMap);
  const dismissedRef = React.useRef<DismissedMap>(dismissed);

  React.useEffect(() => {
    dismissedRef.current = dismissed;
  }, [dismissed]);

  const addDismissal = React.useCallback(
    (conversationId: string) => {
      setDismissed((prev) => {
        const next = { ...prev, [conversationId]: Date.now() };
        writeDismissedMap(next);
        return next;
      });
    },
    [],
  );

  const removeDismissal = React.useCallback(
    (conversationId: string) => {
      setDismissed((prev) => {
        if (prev[conversationId] == null) return prev;
        const next = { ...prev };
        delete next[conversationId];
        writeDismissedMap(next);
        return next;
      });
    },
    [],
  );

  // ---- React Query for raw unread counts ----
  const query = useQuery<UnreadChatData>({
    queryKey: [...UNREAD_CHAT_QUERY_KEY, profileId],
    enabled: !!profileId && sessionReady,
    queryFn: async () => {
      if (!profileId) return { conversations: [], counts: {} };
      const conversations = await runSupabaseQuery(
        () => fetchConversations(profileId),
        'Unable to load chat conversations.',
        'chat.unreadCounts.conversations',
      );

      const directAndGroup = conversations.filter(
        (c) => c.conversation_type === 'direct' || c.conversation_type === 'group',
      );

      const counts: Record<string, number> = {};
      directAndGroup.forEach(conv => { counts[conv.conversation_id] = Number(conv.unread_count ?? 0); });

      return { conversations: directAndGroup, counts };
    },
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  });

  const watchedConversationIds = React.useMemo(
    () => (query.data?.conversations ?? []).map((conv) => conv.conversation_id).filter(Boolean),
    [query.data?.conversations],
  );

  // ---- Realtime: scoped message INSERTs + participant changes ----
  React.useEffect(() => {
    if (!profileId || !sessionReady) return;
    let timer: number | null = null;
    let channel: RealtimeChannel | null = null;
    let closed = false;
    const queueInvalidate = () => {
      invalidateUnreadReads();
      if (typeof document !== 'undefined' && document.hidden) return;
      if (timer != null) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        timer = null;
        queryClient.invalidateQueries({ queryKey: UNREAD_CHAT_QUERY_KEY });
      }, 300);
    };

    const supabase = getSupabaseClient();
    const unsubscribeChannel = () => {
      if (!channel) return;
      try {
        supabase.removeChannel(channel);
      } catch {
        /* noop */
      }
      channel = null;
    };

    const subscribeVisible = () => {
      if (closed || channel) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      const next = supabase
        .channel(`chat-unread-watcher:${profileId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'chat_participants',
            filter: `profile_id=eq.${profileId}`,
          },
          () => {
            recordRealtimeEvent({ source: 'unread-chat-participants', table: 'chat_participants', eventType: '*' });
            queueInvalidate();
          },
        );

      watchedConversationIds.forEach((conversationId) => {
        next.on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'quote_chat_messages',
            filter: `conversation_id=eq.${conversationId}`,
          },
          (payload) => {
            recordRealtimeEvent({ source: 'unread-chat-messages', table: 'quote_chat_messages', eventType: payload.eventType });
            const msg = payload.new as Record<string, unknown> | undefined;
            if (!msg || msg.author_profile_id === profileId) return;
            const convId = msg.conversation_id as string | undefined;
            if (convId && dismissedRef.current[convId] != null) {
              console.log('[chat-dismiss] clearDismissal (new message)', {
                conversationId: convId,
                messageAuthor: msg.author_profile_id,
              });
              removeDismissal(convId);
            }
            queueInvalidate();
          },
        );
      });

      channel = next.subscribe();
    };

    const handleVisibilityChange = () => {
      if (typeof document === 'undefined') return;
      if (document.hidden) {
        unsubscribeChannel();
        if (timer != null) {
          window.clearTimeout(timer);
          timer = null;
        }
        return;
      }
      subscribeVisible();
      queryClient.invalidateQueries({ queryKey: UNREAD_CHAT_QUERY_KEY });
    };

    subscribeVisible();
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      closed = true;
      if (timer != null) {
        window.clearTimeout(timer);
      }
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
      unsubscribeChannel();
    };
  }, [profileId, queryClient, removeDismissal, sessionReady, watchedConversationIds]);

  // Listen for the custom "chat:read" event dispatched by ChatPanel /
  // OrderChatPanel whenever messages are marked as read.  This re-fetches
  // the counts so the bell, FAB badge, and home page all update immediately.
  React.useEffect(() => {
    const handler = () => {
      queryClient.invalidateQueries({ queryKey: UNREAD_CHAT_QUERY_KEY });
    };
    window.addEventListener('chat:read', handler);
    return () => window.removeEventListener('chat:read', handler);
  }, [queryClient]);

  // ---- Effective counts (raw minus dismissed) ----
  const data = query.data ?? { conversations: [], counts: {} };

  const effectiveCounts = React.useMemo(() => {
    const result: Record<string, number> = {};
    for (const [convId, count] of Object.entries(data.counts)) {
      result[convId] = dismissed[convId] != null ? 0 : count;
    }
    return result;
  }, [data.counts, dismissed]);

  const totalUnread = React.useMemo(() => {
    const total = Object.values(effectiveCounts).reduce((sum, c) => sum + c, 0);
    console.log('[chat-dismiss] effectiveCounts', {
      rawCounts: data.counts,
      dismissed,
      effectiveCounts,
      totalUnread: total,
    });
    return total;
  }, [effectiveCounts, data.counts, dismissed]);

  // ---- dismissConversation: hide without marking read ----
  const dismissConversation = React.useCallback(
    (conversationId: string) => {
      console.log('[chat-dismiss] dismissConversation', {
        conversationId,
        timestamp: Date.now(),
        rawCount: data.counts[conversationId] ?? 0,
      });
      addDismissal(conversationId);
    },
    [addDismissal, data.counts],
  );

  // ---- dismissAllConversations: batch dismiss all with unread > 0 ----
  const dismissAllConversations = React.useCallback(() => {
    const toDismiss = Object.entries(data.counts)
      .filter(([, count]) => count > 0)
      .map(([convId]) => convId);
    console.log('[chat-dismiss] dismissAllConversations', {
      count: toDismiss.length,
      conversationIds: toDismiss,
    });
    if (toDismiss.length === 0) return;
    setDismissed((prev) => {
      const next = { ...prev };
      const now = Date.now();
      for (const convId of toDismiss) {
        next[convId] = now;
      }
      writeDismissedMap(next);
      return next;
    });
  }, [data.counts]);

  /**
   * Optimistically mark a conversation as read so the badge count drops
   * immediately without waiting for a server round-trip.
   *
   * Updates three layers:
   * 1. React Query cache (instant UI update)
   * 2. localStorage (survives within the same browser session)
   * 3. Server-side read receipt in quote_chat_message_reads (survives
   *    across refreshes, devices, and localStorage clears)
   *
   * Also clears any prior dismissal for this conversation since the read
   * state now supersedes it.
   */
  const markConversationRead = React.useCallback(
    (conversationId: string) => {
      console.log('[chat-dismiss] markConversationRead (not dismiss)', {
        conversationId,
        wasDismissed: dismissed[conversationId] != null,
      });

      // Clear any prior dismissal -- read supersedes dismiss.
      if (dismissed[conversationId] != null) {
        console.log('[chat-dismiss] clearDismissal (read)', { conversationId });
        removeDismissal(conversationId);
      }

      const key = `chat:lastRead:${profileId}:${conversationId}`;
      let previousReadAt: string | null = null;
      try {
        previousReadAt = localStorage.getItem(key);
      } catch {
        /* noop */
      }

      queryClient.setQueryData<UnreadChatData>([...UNREAD_CHAT_QUERY_KEY, profileId], (prev) => {
        if (!prev) return prev;
        return { ...prev, counts: { ...prev.counts, [conversationId]: 0 } };
      });
      const now = new Date().toISOString();
      try {
        localStorage.setItem(key, now);
      } catch {
        /* noop */
      }

      // Persist a server-side read receipt so the read state survives page
      // refreshes and localStorage clears. Fire-and-forget (don't block UI).
      if (profileId) {
        const supabase = getSupabaseClient();
        supabase
          .from('quote_chat_messages')
          .select('id,created_at')
          .eq('conversation_id', conversationId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle()
          .then(({ data: latestMsg }) => {
            if (latestMsg?.id) {
              // Fix clock-skew: the server generates message.created_at;
              // the client captured `now` earlier.  If the server clock is
              // ahead, the message appears "after" our read marker.  Use
              // the later of the two timestamps (same pattern as
              // ChatPanel.markAllRead).
              const msgCreatedAt = String(latestMsg.created_at);
              const correctedReadAt = msgCreatedAt > now ? msgCreatedAt : now;
              try {
                const existingLs = localStorage.getItem(key);
                if (!existingLs || correctedReadAt > existingLs) {
                  localStorage.setItem(key, correctedReadAt);
                }
              } catch { /* noop */ }

              supabase
                .from('quote_chat_message_reads')
                .upsert(
                  {
                    message_id: latestMsg.id,
                    conversation_id: conversationId,
                    reader_profile_id: profileId,
                    read_at: correctedReadAt,
                  },
                  { onConflict: 'message_id,reader_profile_id', ignoreDuplicates: false },
                )
                .then(async ({ error: receiptError }) => {
                  // The read state is already saved in this browser; only the server copy (other
                  // devices, cleared storage) is missing, so the rest still runs.
                  if (receiptError) console.warn('[chat] Read receipt was not saved', { conversationId, error: receiptError });
                  // Keep dashboard notifications/mentions aligned with chat read state.
                  // We gather message IDs that were unread before this read action so
                  // message-level chat_reply notifications are dismissed consistently.
                  const unreadIds = new Set<string>([latestMsg.id]);
                  try {
                    let unreadQuery = supabase
                      .from('quote_chat_messages')
                      .select('id')
                      .eq('conversation_id', conversationId)
                      .neq('author_profile_id', profileId)
                      .order('created_at', { ascending: false })
                      .limit(300);
                    if (previousReadAt) {
                      unreadQuery = unreadQuery.gt('created_at', previousReadAt);
                    }
                    const unreadRes = await unreadQuery;
                    if (Array.isArray(unreadRes.data)) {
                      unreadRes.data.forEach((row) => {
                        const id = row?.id ? String(row.id) : '';
                        if (id) unreadIds.add(id);
                      });
                    }
                  } catch {
                    /* noop */
                  }

                  void acknowledgeChatRead(Array.from(unreadIds))
                    .then(() => {
                      queryClient.invalidateQueries({ queryKey: dashboardQueryKey });
                    })
                    .catch(() => {
                      /* noop */
                    });

                  // Re-fetch unread counts so any concurrent refetch that
                  // used the stale client-time marker gets replaced.
                  queryClient.invalidateQueries({ queryKey: UNREAD_CHAT_QUERY_KEY });
                  try {
                    window.dispatchEvent(
                      new CustomEvent('chat:read', { detail: { conversationId } }),
                    );
                  } catch { /* noop */ }
                });
            }
          });
      }
    },
    [queryClient, profileId, dismissed, removeDismissal],
  );

  /** Force a fresh count from the server (e.g. after leaving a conversation). */
  const refresh = React.useCallback(() => {
    queryClient.invalidateQueries({ queryKey: UNREAD_CHAT_QUERY_KEY });
  }, [queryClient]);

  return {
    totalUnread,
    counts: effectiveCounts,
    rawCounts: data.counts,
    conversations: data.conversations,
    markConversationRead,
    dismissConversation,
    dismissAllConversations,
    refresh,
    isLoading: query.isLoading,
  };
}
